import { useState, useMemo } from "react";
import { cn, timeToMinutes } from "../lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "./ui/popover";
import { format, parseISO } from "date-fns";
import { Calendar, Clock, FileText } from "lucide-react";

// Event colors - matching Events.jsx and backend
const EVENT_COLORS = {
  blue: { name: "Blue", bg: "bg-blue-100 dark:bg-blue-900/40", border: "border-blue-300 dark:border-blue-700", text: "text-blue-800 dark:text-blue-200", solid: "bg-blue-500" },
  green: { name: "Green", bg: "bg-green-100 dark:bg-green-900/40", border: "border-green-300 dark:border-green-700", text: "text-green-800 dark:text-green-200", solid: "bg-green-500" },
  purple: { name: "Purple", bg: "bg-purple-100 dark:bg-purple-900/40", border: "border-purple-300 dark:border-purple-700", text: "text-purple-800 dark:text-purple-200", solid: "bg-purple-500" },
  red: { name: "Red", bg: "bg-red-100 dark:bg-red-900/40", border: "border-red-300 dark:border-red-700", text: "text-red-800 dark:text-red-200", solid: "bg-red-500" },
  orange: { name: "Orange", bg: "bg-orange-100 dark:bg-orange-900/40", border: "border-orange-300 dark:border-orange-700", text: "text-orange-800 dark:text-orange-200", solid: "bg-orange-500" },
  yellow: { name: "Yellow", bg: "bg-yellow-100 dark:bg-yellow-900/40", border: "border-yellow-300 dark:border-yellow-700", text: "text-yellow-800 dark:text-yellow-200", solid: "bg-yellow-500" },
  teal: { name: "Teal", bg: "bg-teal-100 dark:bg-teal-900/40", border: "border-teal-300 dark:border-teal-700", text: "text-teal-800 dark:text-teal-200", solid: "bg-teal-500" },
  pink: { name: "Pink", bg: "bg-pink-100 dark:bg-pink-900/40", border: "border-pink-300 dark:border-pink-700", text: "text-pink-800 dark:text-pink-200", solid: "bg-pink-500" },
};

const getEventColor = (colorId) => EVENT_COLORS[colorId] || EVENT_COLORS.blue;

// Event popover content component
const EventPopoverContent = ({ event }) => {
  const color = getEventColor(event.color);
  
  return (
    <div className="space-y-3 p-1">
      <div className="flex items-center gap-2">
        <div className={cn("w-3 h-3 rounded-full", color.solid)} />
        <h4 className="font-semibold text-base">{event.name}</h4>
      </div>
      
      <div className="space-y-2 text-sm">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Calendar className="w-4 h-4" />
          <span>{format(parseISO(event.date), "EEEE, MMMM d, yyyy")}</span>
        </div>
        
        <div className="flex items-center gap-2 text-muted-foreground">
          <Clock className="w-4 h-4" />
          <span>{event.start_time} - {event.end_time}</span>
        </div>
        
        {event.notes && (
          <div className="flex items-start gap-2 text-muted-foreground">
            <FileText className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span className="text-sm">{event.notes}</span>
          </div>
        )}
      </div>
    </div>
  );
};

// Single event block component - now accepts lane info
const EventBlock = ({ event, timelineStart, timelineEnd, lane, totalLanes, laneHeight }) => {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const color = getEventColor(event.color);
  
  // Calculate position on timeline
  const openMins = timeToMinutes(timelineStart);
  const closeMins = timeToMinutes(timelineEnd);
  const totalMins = closeMins - openMins;
  
  const startMins = timeToMinutes(event.start_time);
  const endMins = timeToMinutes(event.end_time);
  
  // Clamp to timeline bounds
  const effectiveStart = Math.max(startMins, openMins);
  const effectiveEnd = Math.min(endMins, closeMins);
  
  // If event is completely outside timeline, don't render
  if (effectiveEnd <= openMins || effectiveStart >= closeMins) {
    return null;
  }
  
  const leftPercent = ((effectiveStart - openMins) / totalMins) * 100;
  const widthPercent = ((effectiveEnd - effectiveStart) / totalMins) * 100;
  
  // Add small visual gap between events (1px on each side = 2px total gap)
  const gapPixels = 1;
  
  // Calculate vertical position based on lane
  const topOffset = lane * laneHeight + 2; // 2px padding
  const height = laneHeight - 4; // 4px total vertical padding
  
  return (
    <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
      <PopoverTrigger asChild>
        <div
          className={cn(
            "absolute rounded cursor-pointer transition-all hover:brightness-95 hover:z-10",
            color.bg,
            color.border,
            "border flex items-center justify-center overflow-hidden"
          )}
          style={{
            left: `calc(${leftPercent}% + ${gapPixels}px)`,
            width: `calc(${widthPercent}% - ${gapPixels * 2}px)`,
            top: `${topOffset}px`,
            height: `${height}px`,
            minWidth: "20px",
          }}
          data-testid={`event-block-${event.id}`}
        >
          <span className={cn("text-xs font-medium truncate px-1", color.text)}>
            {event.name}
          </span>
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-72" align="start">
        <EventPopoverContent event={event} />
      </PopoverContent>
    </Popover>
  );
};

// Calculate lanes for overlapping events
const calculateEventLanes = (events) => {
  if (events.length === 0) return { eventsWithLanes: [], totalLanes: 0 };
  
  // Sort events by start time, then by duration (longer events first)
  const sortedEvents = [...events].sort((a, b) => {
    const aStart = timeToMinutes(a.start_time);
    const bStart = timeToMinutes(b.start_time);
    if (aStart !== bStart) return aStart - bStart;
    // If same start, longer event goes first
    const aDuration = timeToMinutes(a.end_time) - aStart;
    const bDuration = timeToMinutes(b.end_time) - bStart;
    return bDuration - aDuration;
  });
  
  // Track end times for each lane
  const laneEndTimes = [];
  const eventsWithLanes = [];
  
  for (const event of sortedEvents) {
    const eventStart = timeToMinutes(event.start_time);
    const eventEnd = timeToMinutes(event.end_time);
    
    // Find the first lane where this event fits (no overlap)
    let assignedLane = -1;
    for (let i = 0; i < laneEndTimes.length; i++) {
      if (laneEndTimes[i] <= eventStart) {
        assignedLane = i;
        break;
      }
    }
    
    // If no existing lane fits, create a new one
    if (assignedLane === -1) {
      assignedLane = laneEndTimes.length;
      laneEndTimes.push(eventEnd);
    } else {
      laneEndTimes[assignedLane] = eventEnd;
    }
    
    eventsWithLanes.push({
      ...event,
      lane: assignedLane,
    });
  }
  
  return {
    eventsWithLanes,
    totalLanes: laneEndTimes.length,
  };
};

// Events row component for DayView
const EventsRow = ({ events, timelineStart, timelineEnd, rowHeight = 28, scrollable = false, tickMarks = [] }) => {
  // Filter events that overlap with the visible timeline and calculate lanes
  const { eventsWithLanes, totalLanes } = useMemo(() => {
    const openMins = timeToMinutes(timelineStart);
    const closeMins = timeToMinutes(timelineEnd);
    
    const visibleEvents = events.filter(event => {
      const startMins = timeToMinutes(event.start_time);
      const endMins = timeToMinutes(event.end_time);
      return endMins > openMins && startMins < closeMins;
    });
    
    return calculateEventLanes(visibleEvents);
  }, [events, timelineStart, timelineEnd]);
  
  if (eventsWithLanes.length === 0) {
    return null;
  }
  
  // Calculate total height based on number of lanes + padding
  const laneHeight = rowHeight;
  const verticalPadding = 8; // 8px top + 8px bottom for timeline area
  const contentHeight = Math.max(totalLanes * laneHeight, rowHeight);
  const totalHeight = contentHeight + (verticalPadding * 2);
  
  return (
    <div 
      className="flex border-b-2 border-border/60 bg-muted/20"
      style={{ height: `${totalHeight}px` }}
      data-testid="events-row"
    >
      {/* Label column */}
      <div className={cn(
        "w-48 flex-shrink-0 px-2 border-r border-border bg-card flex items-center",
        scrollable && "sticky left-0 z-20 shadow-[2px_0_4px_rgba(0,0,0,0.05)]"
      )}>
        <span className="text-sm font-medium text-muted-foreground">Events</span>
      </div>
      
      {/* Timeline area - with vertical padding */}
      {scrollable ? (
        <div className="px-6 flex-shrink-0" style={{ paddingTop: `${verticalPadding}px`, paddingBottom: `${verticalPadding}px` }}>
          <div 
            className="relative"
            style={{ width: `${(timeToMinutes(timelineEnd) - timeToMinutes(timelineStart)) / 60 * 100}px`, height: `${contentHeight}px` }}
          >
            {/* Tick marks */}
            {tickMarks.map((tick, index) => (
              <div
                key={index}
                className={cn(
                  "absolute top-0 bottom-0 pointer-events-none",
                  tick.type === 'hour' && "w-px bg-border/40",
                  tick.type === 'medium' && "w-px bg-border/20",
                  tick.type === 'small' && "w-px bg-border/10"
                )}
                style={{ left: `${tick.percent}%` }}
              />
            ))}
            {/* Event blocks */}
            {eventsWithLanes.map(event => (
              <EventBlock
                key={event.id}
                event={event}
                timelineStart={timelineStart}
                timelineEnd={timelineEnd}
                lane={event.lane}
                totalLanes={totalLanes}
                laneHeight={laneHeight}
              />
            ))}
          </div>
        </div>
      ) : (
        <div 
          className="flex-1 relative mx-6" 
          style={{ height: `${contentHeight}px`, marginTop: `${verticalPadding}px`, marginBottom: `${verticalPadding}px` }}
        >
          {/* Tick marks */}
          {tickMarks.map((tick, index) => (
            <div
              key={index}
              className={cn(
                "absolute top-0 bottom-0 pointer-events-none",
                tick.type === 'hour' && "w-px bg-border/40",
                tick.type === 'medium' && "w-px bg-border/20",
                tick.type === 'small' && "w-px bg-border/10"
              )}
              style={{ left: `${tick.percent}%` }}
            />
          ))}
          {/* Event blocks */}
          {eventsWithLanes.map(event => (
            <EventBlock
              key={event.id}
              event={event}
              timelineStart={timelineStart}
              timelineEnd={timelineEnd}
              lane={event.lane}
              totalLanes={totalLanes}
              laneHeight={laneHeight}
            />
          ))}
        </div>
      )}
      
      {/* Stats column placeholder (to align with employee rows) */}
      <div className="w-24 flex-shrink-0 border-l border-border bg-muted/30" />
    </div>
  );
};

export default EventsRow;
export { EventBlock, EventPopoverContent, getEventColor, EVENT_COLORS, calculateEventLanes };
